import { motion } from "framer-motion";
import { IMAGES, BRAND } from "@/data";
import { Reveal, Eyebrow } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Roots in Baltimore",
    body: "Raised in Baltimore, Maryland, Charles earned a B.S. in Rehabilitation Services from the University of Maryland Eastern Shore and began his career as a social studies teacher at West Baltimore Middle School — experiences that shaped a leadership approach built on discipline, integrity, and service.",
  },
  {
    n: "02",
    title: "Leading under pressure",
    body: "In 2001 he relocated to Los Angeles and entered the entertainment industry, managing complex, time-sensitive operations. It sharpened his ability to lead under pressure and execute with precision — and revealed one truth: every industry depends on transportation.",
  },
  {
    n: "03",
    title: "The promise of Peace Trucking",
    body: "Today Charles leads with a focus on safety, efficiency, communication, and customer satisfaction. His philosophy is straightforward: every shipment is a promise that must be delivered with excellence — trust, opportunity, and peace of mind alongside the freight.",
  },
];

export default function Manifesto() {
  return (
    <section id="story" className="relative py-28 lg:py-40 bg-ink overflow-hidden" data-testid="story">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        <div className="lg:sticky lg:top-32">
          <Reveal><Eyebrow>The Story</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Purpose is the <span className="italic text-gold">only cargo</span> that never gets left behind.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-cream/60 font-light leading-relaxed max-w-md">
              Charles Ransom is the Founder & CEO of Peace Trucking — a leader defined by adaptability
              and service who built the company on a single principle: when freight moves with precision,
              businesses and communities thrive.
            </p>
          </Reveal>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="mt-12 relative overflow-hidden border border-gold/15"
          >
            <img src={IMAGES.ceo} alt="Charles Ransom, Founder" className="w-full h-[560px] object-cover object-[center_20%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
            <div className="absolute bottom-0 p-7">
              <div className="font-serif text-2xl text-cream">{BRAND.owner}</div>
              <div className="text-xs tracking-[0.3em] uppercase text-gold/80 mt-1">Founder & CEO</div>
            </div>
          </motion.div>
        </div>

        <div className="space-y-16">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="relative border-t border-gold/15 pt-8">
                <span className="font-serif italic text-7xl lg:text-8xl text-gold/15 absolute -top-2 right-0 select-none">
                  {c.n}
                </span>
                <div className="text-xs tracking-[0.4em] uppercase text-gold/70">Chapter {c.n}</div>
                <h3 className="mt-4 font-serif text-3xl text-cream font-light max-w-md">{c.title}</h3>
                <p className="mt-5 text-cream/60 font-light leading-relaxed max-w-lg">{c.body}</p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <blockquote className="relative border-l-2 border-gold/60 pl-8 py-2 mt-4">
              <p className="font-serif italic text-2xl lg:text-3xl text-cream font-light leading-snug">
                “At Peace Trucking, we don't just transport freight — we deliver trust, create
                opportunity, and keep America moving.”
              </p>
              <footer className="mt-4 text-xs tracking-[0.35em] uppercase text-gold/80">
                Charles Ransom · Founder & CEO
              </footer>
            </blockquote>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
